import { useRef, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import tw from 'twin.macro';
import { IssueKind, JobEventType } from '../../../__generated__/graphql';
import { Button } from '../../components/button';
import {
  Alert,
  Card,
  CardBody,
  CardHeader,
  Field,
  InlineRow,
  Muted,
  Select,
  Stack,
  Textarea,
} from '../../components/ui';
import {
  useGetPresignedUrlForJobPhotoMutation,
  useRecordJobEventMutation,
} from '../../services/serviceService/__generated__/queries.generated';
import { firstGraphQLError, stayErrorMessage } from '../StayHostPage/stayErrors';
import { ChecklistCard } from './JobTimelineView';
import { uploadJobPhotos } from './jobPhotos';
import {
  checklistItems,
  ISSUE_LABELS,
  missingLabels,
  workerActions,
  type JobTimeline,
} from './jobTimeline';

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

/** How long a check-in waits for the position, the permission prompt included. */
const POSITION_WAIT_MS = 8000;

/**
 * The browser's position when the worker allows it; never blocks a check-in.
 * `timeout` only starts once permission is granted, so an unanswered prompt would
 * hold the check-in forever: after `POSITION_WAIT_MS` it is sent without a place.
 */
function currentPosition(): Promise<{ lat: number; lng: number; accuracyMeters: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise(resolve => {
    const giveUp = setTimeout(() => resolve(null), POSITION_WAIT_MS);
    navigator.geolocation.getCurrentPosition(
      p => {
        clearTimeout(giveUp);
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracyMeters: Math.round(p.coords.accuracy),
        });
      },
      () => {
        clearTimeout(giveUp);
        resolve(null);
      },
      { timeout: 6000, maximumAge: 60000, enableHighAccuracy: true },
    );
  });
}

/**
 * The worker's check-ins on the web (spec 019 W19): On my way, Arrived, Start,
 * checklist photos and items, a problem, and Finish. The app is the main tool;
 * this is the backup. The GPS point is sent when the browser allows it and a
 * mismatch is only flagged, never refused.
 */
export function WorkerJobControls({
  bookingId,
  timeline,
  onRecorded,
}: {
  bookingId: string;
  timeline: JobTimeline;
  /** Reload the booking after a check-in changed its status. */
  onRecorded?: () => void;
}) {
  const [record, { loading }] = useRecordJobEventMutation();
  const [presign] = useGetPresignedUrlForJobPhotoMutation();
  const [busy, setBusy] = useState<string | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [issueKind, setIssueKind] = useState<IssueKind>(IssueKind.Other);
  const [issueNote, setIssueNote] = useState('');
  const [issueFiles, setIssueFiles] = useState<File[]>([]);
  const photoInput = useRef<HTMLInputElement>(null);
  const issueInput = useRef<HTMLInputElement>(null);
  const [photoFor, setPhotoFor] = useState<string | null>(null);
  const actions = workerActions(timeline.jobStatus);
  const items = checklistItems(timeline.checklist);

  const presignOne = async (contentType: string, contentLength: number) => {
    const { data } = await presign({ variables: { bookingId, contentType, contentLength } });
    if (!data?.getPresignedUrlForJobPhoto) throw new Error('The upload could not start');
    return data.getPresignedUrlForJobPhoto;
  };

  const send = async (
    type: JobEventType,
    extra: {
      checklistItemCode?: string;
      photoKeys?: string[];
      issueKind?: IssueKind;
      note?: string;
    } = {},
    label: string = type,
  ) => {
    setBusy(label);
    setMissing([]);
    try {
      const position = await currentPosition();
      await record({
        variables: {
          input: {
            bookingId,
            type,
            capturedAt: new Date().toISOString(),
            ...(position ?? {}),
            ...extra,
          },
        },
      });
      onRecorded?.();
      return true;
    } catch (error) {
      const gql = firstGraphQLError(error);
      if (gql?.extensions?.code === 'CHECKLIST_INCOMPLETE') {
        setMissing(missingLabels(gql.extensions?.details?.missing, items));
      }
      toast.error(stayErrorMessage(error, 'Not sent. Try again.'));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const onPhotos = async (files: FileList | null) => {
    const picked = Array.from(files ?? []);
    if (photoInput.current) photoInput.current.value = '';
    if (!picked.length || !photoFor) return;
    setBusy(`photo:${photoFor}`);
    try {
      const keys = await uploadJobPhotos(picked, presignOne);
      if (
        await send(
          JobEventType.Photo,
          { checklistItemCode: photoFor, photoKeys: keys },
          `photo:${photoFor}`,
        )
      ) {
        toast.success('Photo added');
      }
    } catch (error) {
      toast.error(stayErrorMessage(error, 'The photo did not upload'));
      setBusy(null);
    }
  };

  const onIssue = async () => {
    let photoKeys: string[] | undefined;
    try {
      if (issueFiles.length) photoKeys = await uploadJobPhotos(issueFiles, presignOne);
    } catch (error) {
      toast.error(stayErrorMessage(error, 'The photo did not upload'));
      return;
    }
    if (
      await send(JobEventType.Issue, {
        issueKind,
        note: issueNote.trim() || undefined,
        photoKeys,
      })
    ) {
      toast.success('Problem sent to the customer');
      setIssueNote('');
      setIssueFiles([]);
    }
  };

  const step = (
    type: JobEventType,
    text: string,
    show: boolean,
    theme: 'filled' | 'secondary' = 'secondary',
  ) =>
    show ? (
      <Button
        key={type}
        type="button"
        theme={theme}
        text={text}
        isLoading={busy === type}
        isDisabled={loading && busy !== type}
        onClick={() => send(type)}
        dataTestId={`job-step-${type}`}
      />
    ) : null;

  return (
    <Stack $gap={6} data-testid="worker-job-controls">
      <Card>
        <CardHeader
          title="Check in"
          description="Each step records the time and, if you allow it, where you are."
        />
        <CardBody>
          <InlineRow style={{ flexWrap: 'wrap' }}>
            {step(JobEventType.EnRoute, 'On my way', actions.enRoute)}
            {step(JobEventType.Arrived, 'I arrived', actions.arrived, 'filled')}
            {step(JobEventType.Started, 'Start the job', actions.started, 'filled')}
            {step(JobEventType.Completed, 'Finish the job', actions.completed, 'filled')}
            {!actions.enRoute && !actions.arrived && !actions.started && !actions.completed && (
              <Muted $size="sm">No check-in is due now.</Muted>
            )}
          </InlineRow>
          {missing.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Alert tone="warning" title="Finish the checklist first" data-testid="job-missing">
                {missing.join(', ')}
              </Alert>
            </div>
          )}
        </CardBody>
      </Card>
      <HiddenFileInput
        ref={photoInput}
        type="file"
        accept="image/*"
        multiple
        onChange={e => onPhotos(e.target.files)}
        data-testid="job-photo-input"
      />
      <ChecklistCard
        timeline={timeline}
        actions={code => {
          if (!actions.checklist) return null;
          const item = items.find(i => i.code === code);
          if (!item) return null;
          return item.requiresPhoto ? (
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Add photo"
              isLoading={busy === `photo:${code}`}
              onClick={() => {
                setPhotoFor(code);
                photoInput.current?.click();
              }}
            />
          ) : item.done ? null : (
            <Button
              type="button"
              theme="secondary"
              size="sm"
              text="Mark done"
              isLoading={busy === `item:${code}`}
              onClick={() =>
                send(JobEventType.ChecklistItem, { checklistItemCode: code }, `item:${code}`)
              }
            />
          );
        }}
      />
      {actions.issue && (
        <Card>
          <CardHeader
            title="Report a problem"
            description="The customer and the Provider see it right away."
          />
          <CardBody>
            <Stack $gap={4}>
              <Field label="What happened" htmlFor="issue-kind">
                <Select
                  id="issue-kind"
                  value={issueKind}
                  onChange={e => setIssueKind(e.target.value as IssueKind)}
                >
                  {Object.values(IssueKind).map(kind => (
                    <option key={kind} value={kind}>
                      {ISSUE_LABELS[kind]}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field label="Details" htmlFor="issue-note">
                <Textarea
                  id="issue-note"
                  rows={2}
                  maxLength={1000}
                  value={issueNote}
                  onChange={e => setIssueNote(e.target.value)}
                />
              </Field>
              <HiddenFileInput
                ref={issueInput}
                type="file"
                accept="image/*"
                multiple
                onChange={e => setIssueFiles(Array.from(e.target.files ?? []))}
              />
              <InlineRow>
                <Button
                  type="button"
                  theme="secondary"
                  size="sm"
                  text="Add photos"
                  onClick={() => issueInput.current?.click()}
                />
                <Muted $size="sm">
                  {issueFiles.length ? `${issueFiles.length} photo(s)` : 'Optional'}
                </Muted>
              </InlineRow>
              <div>
                <Button
                  type="button"
                  text="Send the problem"
                  onClick={onIssue}
                  isLoading={busy === JobEventType.Issue}
                  dataTestId="job-issue-send"
                />
              </div>
            </Stack>
          </CardBody>
        </Card>
      )}
    </Stack>
  );
}
